import React, { useEffect, useRef, useState } from 'react';
import { useI18n } from '../lib/i18n';

const HERO_VIDEO = '/hero-new.mp4?v=3';

export const Hero = () => {
  const { t } = useI18n();
  const videoRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = true;
    v.defaultMuted = true;
    v.playsInline = true;

    const tryPlay = async () => {
      try {
        await v.play();
        setIsPlaying(true);
      } catch {
        setIsPlaying(false);
      }
    };

    tryPlay();
    const onPlaying = () => setIsPlaying(true);
    const onPause = () => setIsPlaying(false);
    const onLoaded = () => tryPlay();
    v.addEventListener('playing', onPlaying);
    v.addEventListener('pause', onPause);
    v.addEventListener('loadeddata', onLoaded);
    v.addEventListener('canplay', onLoaded);

    const resume = () => {
      tryPlay();
      window.removeEventListener('touchstart', resume);
      window.removeEventListener('click', resume);
    };
    window.addEventListener('touchstart', resume, { once: true, passive: true });
    window.addEventListener('click', resume, { once: true });

    return () => {
      v.removeEventListener('playing', onPlaying);
      v.removeEventListener('pause', onPause);
      v.removeEventListener('loadeddata', onLoaded);
      v.removeEventListener('canplay', onLoaded);
      window.removeEventListener('touchstart', resume);
      window.removeEventListener('click', resume);
    };
  }, []);

  return (
    <section
      id="home"
      data-testid="hero-section"
      className="relative w-full h-[100svh] min-h-[640px] overflow-hidden bg-ink"
    >
      {/* Original flame video, full-bleed */}
      <video
        ref={videoRef}
        className="absolute inset-0 w-full h-full object-cover bg-ink hero-video"
        autoPlay
        muted
        loop
        playsInline
        webkit-playsinline="true"
        x5-playsinline="true"
        preload="auto"
        controls={false}
        disablePictureInPicture
        disableRemotePlayback
        controlsList="nodownload nofullscreen noremoteplayback noplaybackrate"
        data-testid="hero-video"
      >
        <source src={HERO_VIDEO} type="video/mp4" />
      </video>

      {/* Centred manifesto text + small heart-flame emblem below */}
      <div
        className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none px-6 gap-8"
        data-testid="hero-manifesto-stage"
      >
        <p
          data-testid="hero-manifesto"
          className="font-display font-light text-cream/75 text-center max-w-[24ch] sm:max-w-[34ch] md:max-w-[44ch] text-xl md:text-2xl lg:text-3xl leading-[1.6] tracking-[0.06em] uppercase"
          style={{ whiteSpace: 'pre-line', textShadow: '0 2px 24px rgba(0,0,0,0.65), 0 1px 4px rgba(0,0,0,0.55)' }}
        >
          {t.hero.manifesto}
        </p>
        <div
          aria-hidden="true"
          data-testid="hero-emblem-small"
          style={{
            width: 'min(16vw, 96px)',
            height: 'min(16vw, 96px)',
            backgroundColor: '#FAF6F2',
            opacity: 0.75,
            WebkitMaskImage: 'url(/images/heart-flame-logo.png)',
            maskImage: 'url(/images/heart-flame-logo.png)',
            WebkitMaskSize: 'contain',
            maskSize: 'contain',
            WebkitMaskRepeat: 'no-repeat',
            maskRepeat: 'no-repeat',
            WebkitMaskPosition: 'center',
            maskPosition: 'center',
          }}
        />
      </div>
    </section>
  );
};

export default Hero;
